import {
  fetchScheduledEvents,
  createScheduledEvent,
  updateScheduledEvent,
  updateScheduledEventNotificationStatus,
  deleteScheduledEventNotification,
  fetchScheduledCronJobs,
  createScheduledCronJob,
  updateScheduledCronJob,
  launchScheduledCronJobAutomations,
  deleteScheduledCronJobAutomation,
} from "./scheduledAutomationsApi";
import {
  NOTIFICATION_ACTION_STATUS_OPTIONS,
  AUTOMATION_STATUS_OPTIONS,
  TRIGGER_DATE_OPTIONS,
  TO_EMAIL_SOURCE_FROM_OPTIONS,
  FROM_EMAIL_SOURCE_OPTIONS,
} from "./automationEnums";

// These two configs describe everything that differs between the "Scheduled
// Events" resource and the "Scheduled Cron Jobs" resource, so AutomationTable
// and AutomationFormModal can stay generic instead of being duplicated.

export const EVENT_CONFIG = {
  key: "event",
  label: "Scheduled Event",
  labelPlural: "Scheduled Events",
  idField: "scheduledEventId",
  typeIdField: "eventTypeId",
  typeLabelField: "eventType",
  templateField: "eventTemplate",
  launchInfoField: "notification",
  launchInfoIdField: "id",
  launchInfoCodeField: "eventCode",
  hasCronFields: false,
  api: {
    list: fetchScheduledEvents,
    create: createScheduledEvent,
    update: updateScheduledEvent,
    // events support setting selected items to a specific status
    // (LAUNCHED or ON_HOLD) — see NOTIFICATION_ACTION_STATUS_OPTIONS.
    launch: (ids, status) =>
      updateScheduledEventNotificationStatus(ids, status),
    // DELETE /scheduled-events/notification/{notificationId} — takes the
    // nested notification id (same id getLaunchId returns for this
    // config), NOT scheduledEventId. Only callable once the record has a
    // launch record, same precondition as launch/checkbox selection.
    delete: (notificationId) =>
      deleteScheduledEventNotification(notificationId),
  },
  launchStatusOptions: NOTIFICATION_ACTION_STATUS_OPTIONS,
  // field names on each item returned by GET /admin/scheduled-events/event-types
  typeOption: { idField: "eventId", labelField: "eventName" },
};

export const CRON_JOB_CONFIG = {
  key: "cronJob",
  label: "Scheduled Cron Job",
  labelPlural: "Scheduled Cron Jobs",
  idField: "scheduledCronJobId",
  typeIdField: "cronJobTypeId",
  typeLabelField: "cronJobType",
  templateField: "cronJobTemplate",
  launchInfoField: "automation",
  launchInfoIdField: "automationId",
  launchInfoCodeField: "cronJobCode",
  hasCronFields: true,
  api: {
    list: fetchScheduledCronJobs,
    create: createScheduledCronJob,
    update: updateScheduledCronJob,
    launch: (ids, status) =>
      launchScheduledCronJobAutomations(ids, status),
    // DELETE /scheduled-cron-jobs/automation/{automationId} — takes the
    // nested automation id (same id getLaunchId returns for this config),
    // NOT scheduledCronJobId. Only callable once the record has a launch
    // record, same precondition as launch/checkbox selection.
    delete: (automationId) => deleteScheduledCronJobAutomation(automationId),
  },
  launchStatusOptions: null,
  // field names on each item returned by GET /admin/scheduled-cron-jobs/cron-job-types
  typeOption: { idField: "cronJobId", labelField: "eventName" },
};

// Flattens the response of
// GET /scheduled-cron-jobs/notifications/cron-jobs/{categoryId}
// ( [{ categoryId, categoryName, cronJobTypes: [{ cronJobTypeId,
//   cronJobTypeName, count, scheduledCronJobs: [...] }] }] )
// into:
//   - typeOptions: shaped like CRON_JOB_CONFIG.typeOption
//     ({ cronJobId, eventName }) for the "Cron job type" select
//   - items: every scheduledCronJobs entry across all types, already
//     shaped like a normal CRON_JOB_CONFIG list item (same fields as
//     fetchScheduledCronJobs returns), so it can be fed straight into
//     AutomationTable / ScheduledAutomationTab without remapping.
// categoryId is optional; when passed, only that category's entry is used
// (the endpoint is already filtered server-side, but this guards against
// it ever returning more than one category).
export const flattenCronJobsByCategory = (categories = [], categoryId) => {
  const relevant = categoryId
    ? categories.filter((c) => c.categoryId === categoryId)
    : categories;

  const cronJobTypes = relevant.flatMap((c) => c.cronJobTypes || []);

  const typeOptions = cronJobTypes.map((t) => ({
    cronJobId: t.cronJobTypeId,
    eventName: t.cronJobTypeName,
  }));

  const items = cronJobTypes.flatMap((t) => t.scheduledCronJobs || []);

  return { typeOptions, items };
};

export const emptyTemplate = () => ({
  templateId: null,
  templateName: "",
  subject: "",
  body: "",
  htmlBody: false,
  notificationHeading: "",
  notificationMessage: "",
});

// The launch/status endpoints take the nested notification id (events) or
// automation id (cron jobs) — NOT scheduledEventId/scheduledCronJobId.
export const getLaunchId = (config, item) =>
  item?.[config.launchInfoField]?.[config.launchInfoIdField];

export const emptyRecord = (config) => ({
  [config.idField]: null,
  taskName: "",
  description: "",
  [config.typeIdField]: "",
  notificationTypeIds: [],
  status: "ENABLED",
  toEmailSourceFrom: "",
  fromEmailSource: "",
  [config.templateField]: emptyTemplate(),
  ...(config.hasCronFields
    ? { cronJobExpression: "", triggerDate: "OnDATE", days: "" }
    : {}),
});

// The backend returns display-cased enum strings ("Enabled", "Template",
// "Launched"...) but only accepts the real enum casing ("ENABLED",...) on
// write, and our <select> options are keyed on that real casing. Without
// this, edit dropdowns silently fail to preselect (value doesn't match any
// <option>) and re-submitting an untouched record 400s. This does a
// case-insensitive lookup against the known-good option list and falls back
// if the API value doesn't match anything we know about.
// Normalizes for comparison only ("Respective Employee" and
// "RESPECTIVE_EMPLOYEE" should be treated as the same value even though the
// API and our option lists don't agree on casing/separators).
const normalizeEnumForCompare = (value) =>
  String(value)
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

const matchEnumOption = (value, options, fallback = "") => {
  if (!value) return fallback;
  const found = options.find(
    (opt) => normalizeEnumForCompare(opt) === normalizeEnumForCompare(value),
  );
  return found || fallback;
};

// Turns a raw list/detail API item into the shape the form (and emptyRecord)
// expects, with enum fields normalized so <select>s preselect correctly.
export const toFormRecord = (config, item) => {
  const template = item[config.templateField] || {};
  return {
    [config.idField]: item[config.idField],
    taskName: item.taskName || "",
    description: item.description || "",
    [config.typeIdField]: item[config.typeIdField] ?? "",
    notificationTypeIds: (item.notificationTypes || []).map(
      (nt) => nt.notificationTypeId,
    ),
    status: matchEnumOption(item.status, AUTOMATION_STATUS_OPTIONS, "ENABLED"),
    toEmailSourceFrom: matchEnumOption(
      item.toEmailSourceFrom,
      TO_EMAIL_SOURCE_FROM_OPTIONS,
    ),
    fromEmailSource: matchEnumOption(
      item.fromEmailSource,
      FROM_EMAIL_SOURCE_OPTIONS,
    ),
    [config.templateField]: {
      templateId: template.templateId ?? null,
      templateName: template.templateName || "",
      subject: template.subject || "",
      body: template.body || "",
      htmlBody: template.htmlBody ?? false,
      notificationHeading: template.notificationHeading || "",
      notificationMessage: template.notificationMessage || "",
    },
    ...(config.hasCronFields
      ? {
          cronJobExpression: item.cronJobExpression || "",
          triggerDate: matchEnumOption(
            item.triggerDate,
            TRIGGER_DATE_OPTIONS,
            "OnDATE",
          ),
          days: item.days ?? "",
        }
      : {}),
  };
};

// Whitelists exactly the fields the create/update endpoints expect, instead
// of spreading the whole form object — keeps read-only response fields
// (eventType, notification, notificationTypes, templateCode, templateStatus,
// employeeId, companyId...) that got carried into `form` via toFormRecord's
// caller out of the request body.
export const buildPayload = (config, form) => ({
  [config.idField]: form[config.idField] ?? null,
  taskName: form.taskName,
  description: form.description || null,
  [config.typeIdField]: Number(form[config.typeIdField]),
  notificationTypeIds: form.notificationTypeIds || [],
  status: form.status,
  toEmailSourceFrom: form.toEmailSourceFrom || null,
  fromEmailSource: form.fromEmailSource || null,
  [config.templateField]: form[config.templateField],
  ...(config.hasCronFields
    ? {
        cronJobExpression: form.cronJobExpression,
        triggerDate: form.triggerDate,
        days: form.days === "" ? null : Number(form.days),
      }
    : {}),
});
